/**
 * Determines the collection of measurements and their descriptions for the Selvester ECG task.
 */

import { MeasurementRequirement, LineMeasurementRequirement, DerivedMeasurementCollection, DerivedMeasurement } from "../Measurements/Measurement";

import { Map } from "immutable";
import { TaskData } from "./Task";
import { max, min } from "lodash";
 

// MARK: - Inputs

/**
 * The measurement requirements for the Selvester ECG task.
 */
const _requirements: MeasurementRequirement[] = [
    {
        key: 'calibration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Calibration',
            description: 'The calibration of the ECG. Measure one big square.',
            lead: 'aVL',
            unit: "unitless"
        }
    },
    {
        key: 'avl-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Duration aVL',
            description: 'The duration of the Q wave in aVL in milliseconds.',
            lead: 'aVL',
            unit: "ms"
        }
    },
    {
        key: 'avl-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage aVL',
            description: 'The voltage of the R wave in aVL in millivolts.',
            lead: 'aVL',
            unit: "mV"
        }
    },
    {
        key: 'avl-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage aVL',
            description: 'The voltage of the Q wave in aVL in millivolts.',
            lead: 'aVL',
            unit: "mV"
        }
    },
    {
        key: 'i-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Duration I',
            description: 'The duration of the Q wave in I in milliseconds.',
            lead: 'I',
            unit: "ms"
        }
    },
    {
        key: 'i-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage I',
            description: 'The voltage of the R wave in I in millivolts.',
            lead: 'I',
            unit: "mV"
        }
    },
    {
        key: 'i-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'I Voltage Q',
            description: 'The voltage of the Q wave in I in millivolts.',
            lead: 'I',
            unit: "mV"
        }
    },
    {
        key: 'ii-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Duration II',
            description: 'The duration of the Q wave in II in milliseconds.',
            lead: 'II',
            unit: "ms"
        }
    },
    {
        key: 'avf-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Duration aVF',
            description: 'The duration of the Q wave in aVF in milliseconds.',
            lead: 'aVF',
            unit: "ms"
        }
    },
    {
        key: 'avf-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Duration aVF',
            description: 'The duration of the Q wave in aVF in milliseconds.',
            lead: 'aVF',
            unit: "ms"
        }
    },
    {
        key: 'avf-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage aVF',
            description: 'The voltage of the R wave in aVF in millivolts.',
            lead: 'aVF',
            unit: "mV"
        }
    },
    {
        key: 'v1-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V1',
            description: 'The voltage of the Q wave in V1 in millivolts.',
            lead: 'V1',
            unit: "mV"
        }
    },
    {
        key: 'v1-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V1',
            description: 'The voltage of the R wave in V1 in millivolts.',
            lead: 'V1',
            unit: "mV"
        } 
    },
    {
        key: 'v1-r-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Duration V1',
            description: 'The duration of the R wave in V1 in milliseconds.',
            lead: 'V1',
            unit: "ms"
        } 
    },
    {
        key: 'v1-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V1',
            description: 'The voltage of the Q wave in V1 in millivolts.',
            lead: 'V1',
            unit: "mV"
        } 
    },
    {
        key: 'v1-s-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'S Voltage V1',
            description: 'The voltage of the S wave in V1 in millivolts.',
            lead: 'V1',
            unit: "mV"
        } 
    },
    {
        key: 'v2-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V2',
            description: 'The voltage of the Q wave in V2 in millivolts.',
            lead: 'V2',
            unit: "mV"
        } 
    },
    {
        key: 'v2-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V2',
            description: 'The voltage of the R wave in V2 in millivolts.',
            lead: 'V2',
            unit: "mV"
        } 
    },
    {
        key: 'v2-s-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'S Voltage V2',
            description: 'The voltage of the S wave in V2 in millivolts.',
            lead: 'V2',
            unit: "mV"
        } 
    },
    {
        key: 'v2-r-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Duration V2',
            description: 'The duration of the R wave in V2 in milliseconds.',
            lead: 'V2',
            unit: "ms"
        } 
    },
    {
        key: 'v3-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V3',
            description: 'The voltage of the Q wave in V3 in millivolts.',
            lead: 'V3',
            unit: "mV"
        } 
    },
    {
        key: 'v3-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V3',
            description: 'The voltage of the R wave in V3 in millivolts.',
            lead: 'V3',
            unit: "mV"
        } 
    },
    {
        key: 'v3-r-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Duration V3',
            description: 'The duration of the R wave in V3 in milliseconds.',
            lead: 'V3',
            unit: "ms"
        } 
    },
    {
        key: 'v4-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'q Duration V4',
            description: 'The duration of the Q wave in V4 in milliseconds.',
            lead: 'V4',
            unit: "ms"
        } 
    },
    {
        key: 'v4-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V4',
            description: 'The voltage of the Q wave in V4 in millivolts.',
            lead: 'V4',
            unit: "mV"
        } 
    },
    {
        key: 'v4-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V4',
            description: 'The voltage of the R wave in V4 in millivolts.',
            lead: 'V4',
            unit: "mV"
        } 
    },
    {
        key: 'v4-s-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'S Voltage V4',
            description: 'The voltage of the S wave in V4 in millivolts.',
            lead: 'V4',
            unit: "mV"
        } 
    },
    {
        key: 'v5-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'q Duration V5',
            description: 'The duration of the Q wave in V5 in milliseconds.',
            lead: 'V5',
            unit: "ms"
        } 
    },
    {
        key: 'v5-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V5',
            description: 'The voltage of the Q wave in V5 in millivolts.',
            lead: 'V5',
            unit: "mV"
        } 
    },
    {
        key: 'v5-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V5',
            description: 'The voltage of the R wave in V5 in millivolts.',
            lead: 'V5',
            unit: "mV"
        } 
    },
    {
        key: 'v5-s-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'S Voltage V5',
            description: 'The voltage of the S wave in V5 in millivolts.',
            lead: 'V5',
            unit: "mV"
        } 
    },
    {
        key: 'v6-q-duration',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'q Duration V6',
            description: 'The duration of the Q wave in V6 in milliseconds.',
            lead: 'V6',
            unit: "ms"
        } 
    },
    {
        key: 'v6-q-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'Q Voltage V6',
            description: 'The voltage of the Q wave in V6 in millivolts.',
            lead: 'V6',
            unit: "mV"
        } 
    },
    {
        key: 'v6-r-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'R Voltage V6',
            description: 'The voltage of the R wave in V6 in millivolts.',
            lead: 'V6',
            unit: "mV"
        } 
    },
    {
        key: 'v6-s-voltage',
        type: 'line',
        direction: 'free',
        metadata: {
            name: 'S Voltage V6',
            description: 'The voltage of the S wave in V6 in millivolts.',
            lead: 'V6',
            unit: "mV"
        } 
    }

]

const _derived: DerivedMeasurement[] = [
    // aVL Q > 30ms
    {
        key: 'avl-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 30ms',
            description: 'The duration of the Q wave in aVL is greater or equal to than 30ms.',
            lead: 'aVL',
            unit: "unitless"
        },
        dependencies: ['avl-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('avl-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },

    // aVL R/Q < 1
    {
        key: 'avl-r-q-smaller-than-1',
        type: 'derived',
        dependencies: ['avl-r-voltage', 'avl-q-voltage'],
        metadata: {
            name: 'R/Q <= 1',
            description: 'The voltage of the R wave in aVL is less than or equal to the voltage of the Q wave.',
            lead: 'aVL',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('avl-r-voltage')!;
            const qVoltage = measurements.get('avl-q-voltage')!;

            return rVoltage <= qVoltage ? 1 : 0;
        }
    },

    // aVL Lead Score
    {
        key: 'avl-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead aVL.',
            lead: 'aVL',
            unit: "unitless"
        },
        dependencies: ['avl-r-q-smaller-than-1', 'avl-q-bigger-than-30-ms'],
        default: undefined,
        calculate: (measurements): number => {
            const rQSmallerThan1 = measurements.get('avl-r-q-smaller-than-1')!;
            const qBiggerThan30ms = measurements.get('avl-q-bigger-than-30-ms')!;

            return rQSmallerThan1 + qBiggerThan30ms;
        }
    },

    // I Q > 30ms
    {
        key: 'i-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 30ms',
            description: 'The duration of the Q wave in I is greater or equal to than 30ms.',
            lead: 'I',
            unit: "unitless"
        },
        dependencies: ['i-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('i-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },

    // I R/Q > 1
    {
        key: 'i-r-q-smaller-than-1',
        type: 'derived',
        dependencies: ['i-r-voltage', 'i-q-voltage'],
        metadata: {
            name: 'R/Q <= 1',
            description: 'The voltage of the R wave in I is less than or equal to the voltage of the Q wave.',
            lead: 'I',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('i-r-voltage')!;
            const qVoltage = measurements.get('i-q-voltage')!;

            return rVoltage <= qVoltage ? 1 : 0;
        }
    },

    // I R <= 0.2mV
    {
        key: 'i-r-smaller-than-0.2-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.2mV',
            description: 'The voltage of the R wave in I is less than or equal to 0.2mV.',
            lead: 'I',
            unit: "unitless"
        },
        dependencies: ['i-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('i-r-voltage')!;

            return rVoltage <= 0.2 ? 1 : 0;
        }
    },

    // I Lead Score
    {
        key: 'i-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead I.',
            lead: 'I',
            unit: "unitless"
        },
        dependencies: ['i-q-bigger-than-30-ms', 'i-r-q-smaller-than-1', 'i-r-smaller-than-0.2-mv'],
        default: undefined,
        calculate: (measurements): number => {
            const qBiggerThan30ms = measurements.get('i-q-bigger-than-30-ms')!;
            
            const rQSmallerThan1 = measurements.get('i-r-q-smaller-than-1')!;
            const rSmallerThan02mv = measurements.get('i-r-smaller-than-0.2-mv')!;

            return qBiggerThan30ms + computeBracketScore([rQSmallerThan1, rSmallerThan02mv])!;
        }
    },

    // II Q >= 40ms
    {
        key: 'ii-q-bigger-than-40-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 40ms',
            description: 'The duration of the Q wave in II is greater or equal to than 40ms.',
            lead: 'II',
            unit: "unitless"
        },
        dependencies: ['ii-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('ii-q-duration')!;

            return qDuration >= 40 ? 1 : 0;
        }
    },

    // II Q >= 30ms
    {
        key: 'ii-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 30ms',
            description: 'The duration of the Q wave in II is greater or equal to than 30ms.',
            lead: 'II',
            unit: "unitless"
        },
        dependencies: ['ii-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('ii-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },

    {
        key: 'ii-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead II.',
            lead: 'II',
            unit: "unitless"
        },
        dependencies: ['ii-q-bigger-than-30-ms', 'ii-q-bigger-than-30-ms'],
        default: undefined,
        calculate: (measurements): number => {
            const qBiggerThan30ms = measurements.get('ii-q-bigger-than-30-ms')!;
            const rQSmallerThan1 = measurements.get('ii-q-bigger-than-30-ms')!;

            return qBiggerThan30ms + rQSmallerThan1;
        }
    },
       
    // aVF Q >= 30ms
    {
        key: 'avf-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 30ms',
            description: 'The duration of the Q wave in aVF is greater or equal to than 30ms.',
            lead: 'aVF',
            unit: "unitless"
        },
        dependencies: ['avf-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('avf-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },
           

    // aVF Q >= 40ms
    {
        key: 'avf-q-bigger-than-40-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 40ms',
            description: 'The duration of the Q wave in aVF is greater or equal to than 40ms.',
            lead: 'aVF',
            unit: "unitless"
        },
        dependencies: ['avf-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('avf-q-duration')!;

            return qDuration >= 40 ? 1 : 0;
        }
    },                  

    // aVF Q >= 50ms
    {
        key: 'avf-q-bigger-than-50-ms',
        type: 'derived',
        metadata: {
            name: 'Q >= 50ms',
            description: 'The duration of the Q wave in aVF is greater or equal to than 50ms.',
            lead: 'aVF',
            unit: "unitless"
        },
        dependencies: ['avf-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('avf-q-duration')!;

            return qDuration >= 50 ? 1 : 0;
        }
    },

    // aVF R/Q < 1
    {
        key: 'aVF-r-q-smaller-than-1',
        type: 'derived',
        dependencies: ['avf-r-voltage', 'avf-q-voltage'],
        metadata: {
            name: 'R/Q <= 1',
            description: 'The voltage of the R wave in I is smaller than or equal to the voltage of the Q wave.',
            lead: 'aVF',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('avf-r-voltage')!;
            const qVoltage = measurements.get('avf-q-voltage')!;

            return rVoltage <= qVoltage ? 1 : 0;
        }
    },

    // aVF R/Q < 2
    {
        key: 'aVF-r-q-smaller-than-2',
        type: 'derived',
        dependencies: ['avf-r-voltage', 'avf-q-voltage'],
        metadata: {
            name: 'R/Q <= 2',
            description: 'The voltage of the R wave in I is smaller than or equal to double the voltage of the Q wave.',
            lead: 'aVF',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('avf-r-voltage')!;
            const qVoltage = measurements.get('avf-q-voltage')!;

            return rVoltage <= 2* qVoltage ? 1 : 0;
        }
    },

    // aVF Lead Score
    {
        key: 'avf-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead aVF.',
            lead: 'aVF',
            unit: "unitless"
        },
        dependencies: ['aVF-r-q-smaller-than-2', 'aVF-r-q-smaller-than-1', 'avf-q-bigger-than-50-ms', 'avf-q-bigger-than-40-ms', 'avf-q-bigger-than-30-ms'],
        default: undefined,
        calculate: (measurements): number => {
            const rQSmallerThan2 = measurements.get('aVF-r-q-smaller-than-2')!;
            const rQSmallerThan1 = measurements.get('aVF-r-q-smaller-than-1')!;
            const qBiggerThan50ms = measurements.get('avf-q-bigger-than-50-ms')!;
            const qBiggerThan40ms = measurements.get('avf-q-bigger-than-40-ms')!;
            const qBiggerThan30ms = measurements.get('avf-q-bigger-than-30-ms')!;

            return rQSmallerThan2 + rQSmallerThan1 + qBiggerThan50ms + qBiggerThan40ms + qBiggerThan30ms;
        }
    },

    // V1 any Q
    {
        key: 'V1-any-q',
        type:'derived',
        dependencies: ['v1-q-voltage'],
        metadata: {
            name:'Any q',
            description: 'There is a q wave in V1.',
            lead: 'V1',
            unit:'unitless'
        },
        default: 0,
        calculate: (measurements): number => {
            const qVoltage = measurements.get('v1-q-voltage')!;

            return qVoltage > 0 ? 1 : 0;
        }
    },

    
    // V1 R/S > 1
    {
        key: 'V1-r-s-bigger-than-1',
        type: 'derived',
        dependencies: ['v1-r-voltage', 'v1-s-voltage'],
        metadata: {
            name: 'R/S >= 1',
            description: 'The voltage of the R wave in V1 is greater than or equal to the voltage of the S wave.',
            lead: 'V1',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v1-r-voltage')!;
            const sVoltage = measurements.get('v1-s-voltage')!;

            return rVoltage >= sVoltage ? 1 : 0;
        }
    }, 
    // V1 R >= 50ms
    {
        key: 'v1-r-bigger-than-50-ms',
        type: 'derived',
        metadata: {
            name: 'r >= 50ms',
            description: 'The duration of the r wave in V1 is greater or equal to than 50ms.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['v1-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v1-r-duration')!;

            return rDuration >= 50 ? 2 : 0;
        }
    },   
    // V1 R >= 40ms
    {
        key: 'v1-r-bigger-than-40-ms',
        type: 'derived',
        metadata: {
            name: 'r >= 40ms',
            description: 'The duration of the r wave in V1 is greater or equal to than 40ms.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['v1-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v1-r-duration')!;

            return rDuration >= 40 ? 1 : 0;
        }
    },

    // V1 R >= 1.0mV
    {
        key: 'v1-r-bigger-than-1.0-mv',
        type: 'derived',
        metadata: {
            name: 'R >= 1.0mV',
            description: 'The voltage of the R wave in V1 is greater than or equal to 1.0mV.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['v1-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v1-r-voltage')!;

            return rVoltage >= 1.0 ? 2 : 0;
        }
    },

    // V1 R >= 0.6mV
    {
        key: 'v1-r-bigger-than-0.6-mv',
        type: 'derived',
        metadata: {
            name: 'R >= 1.0mV',
            description: 'The voltage of the R wave in V1 is greater than or equal to 0.6mV.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['v1-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v1-r-voltage')!;

            return rVoltage >= 0.6 ? 1 : 0;
        }
    },

    // V1 Q & S <= 0.4mV
    {
        key: 'v1-q-and-s-smaller-than-0.4-mv',
        type: 'derived',
        metadata: {
            name: 'Q&S <= 0.4mV',
            description: 'The voltages of the Q and S wave in V1 is less than or equal to 0.4mV.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['v1-q-voltage','v1-s-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const qVoltage = measurements.get('v1-q-voltage')!;
            const sVoltage = measurements.get('v1-s-voltage')!;

            return qVoltage <= 0.4 && sVoltage <= 0.4 ? 1 : 0;
        }
    },

    // V1 Lead Score
    {
        key: 'v1-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead V1.',
            lead: 'V1',
            unit: "unitless"
        },
        dependencies: ['V1-any-q', 'V1-r-s-bigger-than-1', 'v1-r-bigger-than-50-ms', 
                       'v1-r-bigger-than-40-ms', 'v1-r-bigger-than-1.0-mv', 'v1-r-bigger-than-0.6-mv', 
                       'v1-q-and-s-smaller-than-0.4-mv'],
        default: undefined,
        calculate: (measurements): number => {
            // V1 anterior

            const anyQ = measurements.get('V1-any-q')!;

            const v1Anterior = anyQ;

            // V1 posterior

            const rSBiggerThan1 = measurements.get('V1-r-s-bigger-than-1')!;
            const qAndSSmallerThan04mv = measurements.get('v1-q-and-s-smaller-than-0.4-mv')!;

            const rBiggerThan50ms = measurements.get('v1-r-bigger-than-50-ms')!;
            const rBiggerThan40ms = measurements.get('v1-r-bigger-than-40-ms')!;
            const rBiggerThan1 = measurements.get('v1-r-bigger-than-1.0-mv')!;
            const rBiggerThan06 = measurements.get('v1-r-bigger-than-0.6-mv')!;

            const bracket = computeBracketScore([rBiggerThan50ms, rBiggerThan40ms, rBiggerThan1, rBiggerThan06])!;

            const v1Posterior = rSBiggerThan1 + qAndSSmallerThan04mv + bracket;

            return v1Anterior + v1Posterior;
        }
    },
    
    // V2 any Q
    {
        key: 'V2-any-q',
        type:'derived',
        dependencies: ['v2-q-voltage'],
        metadata: {
            name:'Any q',
            description: 'There is a q wave in V2.',
            lead: 'V2',
            unit:'unitless'
        },
        default: 0,
        calculate: (measurements): number => {
            const qVoltage = measurements.get('v2-q-voltage')!;

            return qVoltage > 0 ? 1 : 0;
        }
    },

    // V2 R <= 10ms
    {
        key: 'v2-r-smaller-than-10-ms',
        type: 'derived',
        metadata: {
            name: 'r <= 10ms',
            description: 'The duration of the r wave in V2 is less than or equal to 10ms.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v2-r-duration')!;

            return rDuration <= 10 ? 1 : 0;
        }
    },

    // V2 R <= 0.1mV
    {
        key: 'v2-r-smaller-than-0.1-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.1mV',
            description: 'The voltage of the R wave in V2 is less than or equal to 0.1mV.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v2-r-voltage')!;

            return rVoltage <= 0.1 ? 1 : 0;
        }
    },

    // V2 R <= V1 R
    {
        key: 'v2-r-smaller-V1-r',
        type: 'derived',
        metadata: {
            name: 'V2R < V1R',
            description: 'The voltage of the R wave in V2 is less than or equal the voltage of the R wave in V1.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-voltage', 'v1-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rv1Voltage = measurements.get('v1-r-voltage')!;
            const rv2Voltage = measurements.get('v2-r-voltage')!;

            return rv2Voltage <= rv1Voltage ? 1 : 0;
        }
    },

    // V2 R/S > 1.5
    {
        key: 'V2-r-s-bigger-than-1.5',
        type: 'derived',
        dependencies: ['v2-r-voltage', 'v2-s-voltage'],
        metadata: {
            name: 'R/S >= 1.5',
            description: 'The voltage of the R wave in V2 is greater than or equal to 1.5 times the voltage of the S wave.',
            lead: 'V2',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v1-r-voltage')!;
            const sVoltage = measurements.get('v1-s-voltage')!;

            return rVoltage >= 1.5* sVoltage ? 1 : 0;
        }
    }, 

    // V2 R >= 60ms
    {
        key: 'v2-r-bigger-than-60-ms',
        type: 'derived',
        metadata: {
            name: 'r >= 60ms',
            description: 'The duration of the r wave in V2 is greater than or equal to 60ms.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v2-r-duration')!;

            return rDuration >= 60 ? 2 : 0;
        }
    },

    // V2 R >= 50ms
    {
        key: 'v2-r-bigger-than-50-ms',
        type: 'derived',
        metadata: {
            name: 'r >= 60ms',
            description: 'The duration of the r wave in V2 is greater than or equal to 50ms.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v2-r-duration')!;

            return rDuration >= 50 ? 1 : 0;
        }
    },

    // V2 R >= 2.0mV
    {
        key: 'v2-r-bigger-than-2.0-mv',
        type: 'derived',
        metadata: {
            name: 'R >= 2.0mV',
            description: 'The voltage of the R wave in V2 is greater than or equal to 2.0mV.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v2-r-voltage')!;

            return rVoltage >= 2.0 ? 2 : 0;
        }
    },
    
    // V2 R >= 1.5mV
    {
        key: 'v2-r-bigger-than-1.5-mv',
        type: 'derived',
        metadata: {
            name: 'R >= 1.5mV',
            description: 'The voltage of the R wave in V2 is greater than or equal to 1.5mV.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v2-r-voltage')!;

            return rVoltage >= 1.5 ? 1 : 0;
        }
    },  

    // V2 Q & S <= 0.4mV
    {
        key: 'v2-q-and-s-smaller-than-0.4-mv',
        type: 'derived',
        metadata: {
            name: 'Q&S <= 0.4mV',
            description: 'The voltages of the Q and S wave in V2 is less than or equal to 0.4mV.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['v2-q-voltage','v2-s-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const qVoltage = measurements.get('v2-q-voltage')!;
            const sVoltage = measurements.get('v2-s-voltage')!;

            return qVoltage <= 0.4 && sVoltage <= 0.4 ? 1 : 0;
        }
    },

    // V2 Lead Score
    {
        key: 'v2-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead V2.',
            lead: 'V2',
            unit: "unitless"
        },
        dependencies: ['V2-any-q', 'v2-r-smaller-than-10-ms', 'v2-r-smaller-than-0.1-mv', 
                       'v2-r-smaller-V1-r', 'V2-r-s-bigger-than-1.5', 'v2-r-bigger-than-60-ms',
                       'v2-r-bigger-than-50-ms', 'v2-r-bigger-than-2.0-mv', 'v2-r-bigger-than-1.5-mv',
                       'v2-q-and-s-smaller-than-0.4-mv'],
        default: undefined,
        calculate: (measurements): number => {
            // V2 anterior
            const anyQ = measurements.get('V2-any-q')!;
            const v2RSmallerThan10 = measurements.get('v2-r-smaller-than-10-ms')!;
            const v2RSmallerThan01 = measurements.get('v2-r-smaller-than-0.1-mv')!;
            const v2RSmallerThanV1R = measurements.get('v2-r-smaller-V1-r')!;

            const v2Anterior = max([anyQ, v2RSmallerThan10, v2RSmallerThan01, v2RSmallerThanV1R])!;

            // V2 posterior

            const rsBiggerThan15 = measurements.get('V2-r-s-bigger-than-1.5')!;
            const qAndSSmallerThan04mv = measurements.get('v2-q-and-s-smaller-than-0.4-mv')!;

            const rBiggerThan60ms = measurements.get('v2-r-bigger-than-60-ms')!;
            const rBiggerThan50ms = measurements.get('v2-r-bigger-than-50-ms')!;
            const rBiggerThan2 = measurements.get('v2-r-bigger-than-2.0-mv')!;
            const rBiggerThan15 = measurements.get('v2-r-bigger-than-1.5-mv')!;

            const bracket = computeBracketScore([
                rBiggerThan60ms, rBiggerThan50ms, rBiggerThan2, rBiggerThan15
            ])!;

            const v2Posterior = rsBiggerThan15 + qAndSSmallerThan04mv + bracket;

            return v2Anterior + v2Posterior;
        }
    },

    // V3 any Q
    {
        key: 'V3-any-q',
        type:'derived',
        dependencies: ['v3-q-voltage'],
        metadata: {
            name:'Any q',
            description: 'There is a q wave in V3.',
            lead: 'V3',
            unit:'unitless'
        },
        default: 0,
        calculate: (measurements): number => {
            const qVoltage = measurements.get('v3-q-voltage')!;

            return qVoltage > 0 ? 1 : 0;
        }
    },
    // V3 R <= 20ms
    {
        key: 'v3-r-smaller-than-20-ms',
        type: 'derived',
        metadata: {
            name: 'r <= 20ms',
            description: 'The duration of the r wave in V3 is less than or equal to 20ms.',
            lead: 'V3',
            unit: "unitless"
        },
        dependencies: ['v3-r-duration'],
        default: 0,
        calculate: (measurements): number => {
            const rDuration = measurements.get('v3-r-duration')!;

            return rDuration <= 20 ? 1 : 0;
        }
    },
    // V3 R <= 0.2mV
    {
        key: 'v3-r-smaller-than-0.2-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.2mV',
            description: 'The voltage of the R wave in V3 is less than or equal to 0.2mV.',
            lead: 'V3',
            unit: "unitless"
        },
        dependencies: ['v3-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v3-r-voltage')!;

            return rVoltage <= 0.2 ? 1 : 0;
        }
    },
     // V3 Lead Score
     {
        key: 'v3-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead V3.',
            lead: 'V3',
            unit: "unitless"
        },
        dependencies: ['V3-any-q','v3-r-smaller-than-20-ms','v3-r-smaller-than-0.2-mv'],
        default: undefined,
        calculate: (measurements): number => {
            
            const v3anyQ = measurements.get('V3-any-q')!;
            const v3RSmallerThan20 = measurements.get('v3-r-smaller-than-20-ms')!;
            const v3RSmallerThan02 = measurements.get('v3-r-smaller-than-0.2-mv')!;

            return computeBracketScore([v3anyQ, v3RSmallerThan20, v3RSmallerThan02]);
        
        },
    },


    // V4 Q >= 20ms
    {
        key: 'v4-q-bigger-than-20-ms',
        type: 'derived',
        metadata: {
            name: 'V4 >= 20ms',
            description: 'The duration of the Q wave in V4 is greater or equal to than 20ms.',
            lead: 'V4',
            unit: "unitless"
        },
        dependencies: ['v4-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('v4-q-duration')!;

            return qDuration >= 20 ? 1 : 0;
        }
    },

    // V4 R/S < 0.5
    {
        key: 'V4-r-s-smaller-than-0.5',
        type: 'derived',
        dependencies: ['v4-r-voltage', 'v4-s-voltage'],
        metadata: {
            name: 'R/S <= 0.5',
            description: 'The voltage of the R wave in V4 is less than or equal to 0.5 times the voltage of the S wave.',
            lead: 'V4',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v4-r-voltage')!;
            const sVoltage = measurements.get('v4-s-voltage')!;

            return rVoltage <= 0.5* sVoltage ? 2 : 0;
        }
    }, 

    // V4 R/Q < 0.5
    {
        key: 'V4-r-q-smaller-than-0.5',
        type: 'derived',
        dependencies: ['v4-r-voltage', 'v4-q-voltage'],
        metadata: {
            name: 'R/Q <= 0.5',
            description: 'The voltage of the R wave in V4 is less than or equal to 0.5 times the voltage of the Q wave.',
            lead: 'V4',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v4-r-voltage')!;
            const qVoltage = measurements.get('v4-q-voltage')!;

            return rVoltage <= 0.5* qVoltage ? 2 : 0;
        }
    },

    // V4 R/S < 1
    {
        key: 'V4-r-s-smaller-than-1',
        type: 'derived',
        dependencies: ['v4-r-voltage', 'v4-s-voltage'],
        metadata: {
            name: 'R/S <= 1.0',
            description: 'The voltage of the R wave in V4 is less than or equal the voltage of the S wave.',
            lead: 'V4',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v4-r-voltage')!;
            const sVoltage = measurements.get('v4-s-voltage')!;

            return rVoltage <= sVoltage ? 1 : 0;
        }
    },
    
    // V4 R/Q < 1.0
    {
        key: 'V4-r-q-smaller-than-1.0',
        type: 'derived',
        dependencies: ['v4-r-voltage', 'v4-q-voltage'],
        metadata: {
            name: 'R/Q <= 1.0',
            description: 'The voltage of the R wave in V4 is less than or equal the voltage of the Q wave.',
            lead: 'V4',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v4-r-voltage')!;
            const qVoltage = measurements.get('v4-q-voltage')!;

            return rVoltage <= qVoltage ? 1 : 0;
        }
    },

    // V4 R <= 0.7mV
    {
        key: 'v4-r-smaller-than-0.7-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.7mV',
            description: 'The voltage of the R wave in V4 is less than or equal to 0.7mV.',
            lead: 'V4',
            unit: "unitless"
        },
        dependencies: ['v4-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v4-r-voltage')!;

            return rVoltage <= 0.7 ? 1 : 0;
        }
    },

    // V4 Lead Score
    {
        key: 'v4-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead V4.',
            lead: 'V4',
            unit: "unitless"
        },
        dependencies: ['v4-q-bigger-than-20-ms','V4-r-s-smaller-than-0.5','V4-r-q-smaller-than-0.5','V4-r-s-smaller-than-1','V4-r-q-smaller-than-1.0','v4-r-smaller-than-0.7-mv'],
        default: undefined,
        calculate: (measurements): number => {
            
            const v4Qbiggerthan20 = measurements.get('V4-q-bigger-than-20-ms')!;
            const v4RSSmallerThan05 = measurements.get('V4-r-s-smaller-than-0.5')!;
            const v4RQSmallerThan05 = measurements.get('V4-r-q-smaller-than-0.5')!;
            const v4RSSmallerThan10 = measurements.get('V4-r-s-smaller-than-1')!;
            const v4RQSmallerThan10 = measurements.get('V4-r-q-smaller-than-1.0')!;
            const v4RSmallerThan07 = measurements.get('v4-r-smaller-than-0.7-mv')!;



            return v4Qbiggerthan20 + computeBracketScore([v4RSSmallerThan05, v4RQSmallerThan05, v4RSSmallerThan10, v4RQSmallerThan10, v4RSmallerThan07])!;
        
        },
    },



    // V5 Q >= 30ms
    {
        key: 'v5-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: 'V5>= 30ms',
            description: 'The duration of the Q wave in V5 is greater or equal to than 30ms.',
            lead: 'V5',
            unit: "unitless"
        },
        dependencies: ['v5-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('v5-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },

    // V5 R/S < 1
    {
        key: 'V5-r-s-smaller-than-1',
        type: 'derived',
        dependencies: ['v5-r-voltage', 'v5-s-voltage'],
        metadata: {
            name: 'R/S <= 1.0',
            description: 'The voltage of the R wave in V5 is less than or equal the voltage of the S wave.',
            lead: 'V5',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v5-r-voltage')!;
            const sVoltage = measurements.get('v5-s-voltage')!;

            return rVoltage <= sVoltage ? 2 : 0;
        }
    },

    // V5 R/Q < 1.0
    {
        key: 'V5-r-q-smaller-than-1.0',
        type: 'derived',
        dependencies: ['v5-r-voltage', 'v5-q-voltage'],
        metadata: {
            name: 'R/Q <= 1.0',
            description: 'The voltage of the R wave in V5 is less than or equal the voltage of the Q wave.',
            lead: 'V5',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v5-r-voltage')!;
            const qVoltage = measurements.get('v5-q-voltage')!;

            return rVoltage <= qVoltage ? 2 : 0;
        }
    },

    // V5 R/S < 2
    {
        key: 'V5-r-s-smaller-than-2',
        type: 'derived',
        dependencies: ['v5-r-voltage', 'v5-s-voltage'],
        metadata: {
            name: 'R/S <= 2.0',
            description: 'The voltage of the R wave in V5 is less than or equal to 2 times the voltage of the S wave.',
            lead: 'V5',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v5-r-voltage')!;
            const sVoltage = measurements.get('v5-s-voltage')!;

            return rVoltage <= 2*sVoltage ? 1 : 0;
        }
    },

    // V5 R/Q < 2.0
    {
        key: 'V5-r-q-smaller-than-2.0',
        type: 'derived',
        dependencies: ['v5-r-voltage', 'v5-q-voltage'],
        metadata: {
            name: 'R/Q <= 2.0',
            description: 'The voltage of the R wave in V5 is less than or equal to 2 times the voltage of the Q wave.',
            lead: 'V5',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v5-r-voltage')!;
            const qVoltage = measurements.get('v5-q-voltage')!;

            return rVoltage <= 2*qVoltage ? 1 : 0;
        }
    },

    // V5 R <= 0.7mV
    {
        key: 'v5-r-smaller-than-0.7-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.7mV',
            description: 'The voltage of the R wave in V5 is less than or equal to 0.7mV.',
            lead: 'V5',
            unit: "unitless"
        },
        dependencies: ['v5-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v5-r-voltage')!;

            return rVoltage <= 0.7 ? 1 : 0;
        }
    },

    // V5 Lead Score
    {
        key: 'v5-lead-score',
        type: 'derived',
        metadata: {
            name: 'Lead Score',
            description: 'The score for lead V5.',
            lead: 'V5',
            unit: "unitless"
        },
        dependencies: ['v5-q-bigger-than-30-ms','V5-r-s-smaller-than-1','V5-r-q-smaller-than-1.0','V5-r-s-smaller-than-2','V5-r-q-smaller-than-2.0','v5-r-smaller-than-0.7-mv'],
        default: undefined,
        calculate: (measurements): number => {
            
            const v5Qbiggerthan30 = measurements.get('V5-q-bigger-than-30-ms')!;
            const v5RSSmallerThan1 = measurements.get('V5-r-s-smaller-than-1')!;
            const v5RQSmallerThan1 = measurements.get('V5-r-q-smaller-than-1.0')!;
            const v5RSSmallerThan2 = measurements.get('V5-r-s-smaller-than-2')!;
            const v5RQSmallerThan2 = measurements.get('V5-r-q-smaller-than-2.0')!;
            const v5RSmallerThan07 = measurements.get('v5-r-smaller-than-0.7-mv')!;

            return v5Qbiggerthan30 + computeBracketScore([v5RQSmallerThan1,v5RSSmallerThan1,v5RQSmallerThan2,v5RSSmallerThan2,v5RSmallerThan07])!;
        
        },
    },

    // V6 Q >= 30ms
    {
        key: 'v6-q-bigger-than-30-ms',
        type: 'derived',
        metadata: {
            name: '65>= 30ms',
            description: 'The duration of the Q wave in V5 is greater or equal to than 30ms.',
            lead: 'V5',
            unit: "unitless"
        },
        dependencies: ['v5-q-duration'],
        default: 0,
        calculate: (measurements): number => {
            const qDuration = measurements.get('v5-q-duration')!;

            return qDuration >= 30 ? 1 : 0;
        }
    },
    // V6 R/S < 1
    {
        key: 'V6-r-s-smaller-than-1',
        type: 'derived',
        dependencies: ['v6-r-voltage', 'v6-s-voltage'],
        metadata: {
            name: 'R/S <= 1.0',
            description: 'The voltage of the R wave in V6 is less than or equal the voltage of the S wave.',
            lead: 'V6',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v6-r-voltage')!;
            const sVoltage = measurements.get('v6-s-voltage')!;

            return rVoltage <= sVoltage ? 2 : 0;
        }
    },

    // V6 R/Q < 1.0
    {
        key: 'V6-r-q-smaller-than-1.0',
        type: 'derived',
        dependencies: ['v6-r-voltage', 'v6-q-voltage'],
        metadata: {
            name: 'R/Q <= 1.0',
            description: 'The voltage of the R wave in V6 is less than or equal the voltage of the Q wave.',
            lead: 'V6',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v6-r-voltage')!;
            const qVoltage = measurements.get('v6-q-voltage')!;

            return rVoltage <= qVoltage ? 2 : 0;
        }
    },

    // V6 R/S < 3
    {
        key: 'V6-r-s-smaller-than-3',
        type: 'derived',
        dependencies: ['v6-r-voltage', 'v6-s-voltage'],
        metadata: {
            name: 'R/S <= 3.0',
            description: 'The voltage of the R wave in V6 is less than or equal to 3 times the voltage of the S wave.',
            lead: 'V6',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v6-r-voltage')!;
            const sVoltage = measurements.get('v6-s-voltage')!;

            return rVoltage <= 3*sVoltage ? 1 : 0;
        }
    },

    // V6 R/Q < 3.0
    {
        key: 'V6-r-q-smaller-than-3.0',
        type: 'derived',
        dependencies: ['v6-r-voltage', 'v6-q-voltage'],
        metadata: {
            name: 'R/Q <= 6.0',
            description: 'The voltage of the R wave in V6 is less than or equal to 3 times the voltage of the Q wave.',
            lead: 'V6',
            unit: "unitless"
        },
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v6-r-voltage')!;
            const qVoltage = measurements.get('v6-q-voltage')!;

            return rVoltage <= 3*qVoltage ? 1 : 0;
        }
    },

    // V6 R <= 0.6mV
    {
        key: 'v6-r-smaller-than-0.6-mv',
        type: 'derived',
        metadata: {
            name: 'R <= 0.6mV',
            description: 'The voltage of the R wave in V6 is less than or equal to 0.6mV.',
            lead: 'V6',
            unit: "unitless"
        },
        dependencies: ['v6-r-voltage'],
        default: 0,
        calculate: (measurements): number => {
            const rVoltage = measurements.get('v6-r-voltage')!;

            return rVoltage <= 0.6 ? 1 : 0;
        }
    },

        // V6 Lead Score
        {
            key: 'v6-lead-score',
            type: 'derived',
            metadata: {
                name: 'Lead Score',
                description: 'The score for lead V6.',
                lead: 'V6',
                unit: "unitless"
            },
            dependencies: ['v6-q-bigger-than-30-ms','V6-r-s-smaller-than-1','V6-r-q-smaller-than-1.0','V6-r-s-smaller-than-3','V5-r-q-smaller-than-3.0','v5-r-smaller-than-0.6-mv'],
            default: undefined,
            calculate: (measurements): number => {
                
                const v6Qbiggerthan50 = measurements.get('V6-q-bigger-than-30-ms')!;
                const v6RSSmallerThan1 = measurements.get('V6-r-s-smaller-than-1')!;
                const v6RQSmallerThan1 = measurements.get('V6-r-q-smaller-than-1.0')!;
                const v6RSSmallerThan3 = measurements.get('V6-r-s-smaller-than-3')!;
                const v6RQSmallerThan3 = measurements.get('V6-r-q-smaller-than-3.0')!;
                const v6RSmallerThan07 = measurements.get('v6-r-smaller-than-0.6-mv')!;
    
    
    
                return v6Qbiggerthan50 + computeBracketScore([v6RSSmallerThan1,v6RQSmallerThan1,v6RSSmallerThan3,v6RQSmallerThan3,v6RSmallerThan07])!;
            
            },
        },
    
]
function computeBracketScore(values: Array<number>): number {
    return max(values)!;
}

// MARK: - Final export

/**
 * The Selvester ECG task data.
 */
const SelvesterTaskData = {
    identifier: 'selvester',
    requirements: _requirements,
    derivedMeasurements: Map(_derived.map((measurement) => [measurement.key, measurement]))
} as TaskData;

export default SelvesterTaskData;
